import { ColDef } from 'ag-grid-community';
import { RevisionTemplateListResponse } from 'api/actions/revision-template-list/revision-template-list-response';
import DataTable from 'components/tables/DataTable';
import { useApi } from 'api/api-context';
import { useCallback, useEffect, useMemo, useState } from 'react';
import RevisionTemplateIdColumn from 'components/tables/data/revision-template/columns/RevisionTemplateIdColumn';
import RevisionTemplateNameColumn from 'components/tables/data/revision-template/columns/RevisionTemplateNameColumn';
import RestrictionsColumn from 'components/tables/data/revision-template/columns/RestrictionsColumn';
import ActionsColumn from 'components/tables/data/revision-template/columns/ActionsColumn';
import SupportsKepColumn from 'components/tables/data/revision-template/columns/SupportsKepColumn';
import TwoRestrictionsSelectFilter from 'components/tables/filters/restriction/TwoRestrictionsSelectFilter';
import DeviceTypeSelectFilter from 'components/tables/filters/DeviceTypeSelectFilter';
import SupportsKepSelectFilter from 'components/tables/filters/SupportsKepSelectFilter';
import DeviceTypeNameColumn from 'components/tables/data/revision-template/columns/DeviceTypeNameColumn';
import { useSearchParams } from 'react-router-dom';
import { RevisionTemplateRow } from 'components/tables/data/revision-template/types';
import { ADD_REVISION_TEMPLATE_PAGE_PATH } from 'routes/paths';
import RevisionTemplateKindColumn from 'components/tables/data/revision-template/columns/RevisionTemplateKindColumn';
import RevisionTemplateKindSelectFilter from 'components/tables/filters/RevisionTemplateKindSelectFilter';
import { getKindsPretty } from 'components/tables/data/kind';
import { getResponsiveActionsColumnWidth, useResponsiveDesign } from 'hooks/use-responsive-design';

type RevisionTemplate = RevisionTemplateListResponse[number];

/**
 * Returns the restriction for the revision template.
 */
function getRevisionTemplateRestriction(template: RevisionTemplate): string {
  if (template.isGlobal) {
    return 'global';
  }

  if (template.isOrganization) {
    return 'organization';
  }

  return 'unknown';
}

/**
 * Returns the restriction for the revision template in a pretty format.
 */
function getPrettyRevisionTemplateRestriction(template: RevisionTemplate): string {
  if (template.isGlobal) {
    return 'Globálne';
  }

  if (template.isOrganization) {
    return 'Organizácia';
  }

  return 'Neznáme';
}

/**
 * Table which lists all revision templates.
 */
export default function RevisionTemplatesTable() {
  const { getAction } = useApi();
  const { tableActionMode } = useResponsiveDesign();
  const [searchParams, setSearchParams] = useSearchParams();

  const [initialFilters] = useState(() => {
    const filters: Record<string, any> = {};

    if (searchParams.has('deviceTypeId')) {
      filters['deviceType.deviceTypeId'] = {
        filterType: 'text',
        type: 'equals',
        filter: searchParams.get('deviceTypeId'),
      };
    }

    return filters;
  });

  const action = useCallback(async () => {
    const action = getAction('RevisionTemplateList');

    const templates = await action({});

    return templates.map((template) => ({
      ...template,
      kindPretty: getKindsPretty(template.kinds),
      kindFilterValue: template.kinds.join(','),
      restriction: getRevisionTemplateRestriction(template),
      restrictionPretty: getPrettyRevisionTemplateRestriction(template),
      restrictedOrganizations: template.organizations
        ? template.organizations
            .map(({ organizationId, organizationName }) => `ID ${organizationId}: ${organizationName}`)
            .join(', ')
        : '',
      blacklistedOrganizationsPretty: (template.blacklistedOrganizations ?? [])
        .map(({ organizationId, organizationName }) => `ID ${organizationId}: ${organizationName}`)
        .join(', '),
    }));
  }, [getAction]);

  useEffect(() => {
    setSearchParams(
      (params) => {
        params.delete('deviceTypeId');
        return params;
      },
      { replace: true }
    );
  }, []);

  const columns = useMemo<ColDef[]>(
    () => [
      {
        field: 'revisionTemplateId',
        headerName: 'ID',
        sortable: true,
        filter: true,
        unSortIcon: true,
        suppressMovable: true,
        width: 76,
        minWidth: 76,
        maxWidth: 76,
        resizable: false,
        cellRenderer: RevisionTemplateIdColumn,
      },
      {
        field: 'name',
        headerName: 'Názov',
        minWidth: 350,
        sortable: true,
        filter: true,
        unSortIcon: true,
        cellRenderer: RevisionTemplateNameColumn,
      },
      {
        field: 'kindPretty',
        headerName: 'Typ šablóny',
        minWidth: 220,
        sortable: true,
        filter: true,
        floatingFilter: true,
        floatingFilterComponent: RevisionTemplateKindSelectFilter,
        unSortIcon: true,
        cellRenderer: RevisionTemplateKindColumn,
      },
      {
        field: 'kindFilterValue',
        headerName: 'Typ šablóny',
        filter: true,
        hide: true,
      },
      {
        field: 'deviceType.deviceTypeId',
        headerName: 'ID zariadenia',
        filter: true,
        hide: true,
      },
      {
        field: 'deviceType.deviceTypeName',
        headerName: 'Zariadenie',
        minWidth: 220,
        sortable: true,
        unSortIcon: true,
        filter: true,
        floatingFilter: true,
        floatingFilterComponent: DeviceTypeSelectFilter,
        cellRenderer: DeviceTypeNameColumn,
      },
      {
        field: 'restriction',
        headerName: 'Vymedzenia',
        minWidth: 500,
        sortable: true,
        unSortIcon: true,
        filter: true,
        floatingFilter: true,
        floatingFilterComponent: TwoRestrictionsSelectFilter,
        cellRenderer: RestrictionsColumn,
        autoHeight: true,
        getQuickFilterText: ({ data }) =>
          [data.restrictionPretty, data.restrictedOrganizations, data.blacklistedOrganizationsPretty].join(' '),
      },
      {
        field: 'restrictionPretty',
        headerName: 'Vymedzenia',
        hide: true,
      },
      {
        field: 'restrictedOrganizations',
        headerName: 'Organizácie',
        hide: true,
      },
      {
        field: 'blacklistedOrganizationsPretty',
        headerName: 'Nedostupná pre organizácie',
        hide: true,
      },
      {
        valueGetter: ({ data: { status } }: { data: RevisionTemplateRow }) => (status ? '1' : '0'),
        field: 'status',
        headerName: 'Aktívna',
        filter: true,
        hide: true,
      },
      {
        valueGetter: ({ data: { supportsKep } }: { data: RevisionTemplateRow }) => (supportsKep ? '1' : '0'),
        field: 'supportsKep',
        headerName: 'Podporuje KEP',
        minWidth: 180,
        sortable: true,
        filter: true,
        floatingFilter: true,
        floatingFilterComponent: SupportsKepSelectFilter,
        unSortIcon: true,
        cellRenderer: SupportsKepColumn,
      },
      {
        field: '_actions',
        headerName: '',
        pinned: 'right',
        width: getResponsiveActionsColumnWidth(230, tableActionMode),
        minWidth: getResponsiveActionsColumnWidth(230, tableActionMode),
        maxWidth: getResponsiveActionsColumnWidth(230, tableActionMode),
        cellRenderer: ActionsColumn,
      },
    ],
    [tableActionMode]
  );

  return (
    <DataTable
      tableId="revision-templates"
      title="Šablóny revíznych správ"
      toggleDiscardedLabel="Zobraziť vyradené"
      columns={columns}
      action={action}
      initialFilters={initialFilters}
      addButtonText="Pridať šablónu"
      addButtonTarget={ADD_REVISION_TEMPLATE_PAGE_PATH.original}
      dataExport={{
        modalTitle: 'Exportovať šablóny revíznych správ',
        fileName: 'sablony-reviznych-sprav.xlsx',
        columnReplacements: {
          restriction: ['restrictionPretty', 'restrictedOrganizations', 'blacklistedOrganizationsPretty'],
        },
        columnKeys: [
          'revisionTemplateId',
          'name',
          'kindPretty',
          'restrictionPretty',
          'restrictedOrganizations',
          'blacklistedOrganizationsPretty',
          'deviceType.deviceTypeId',
          'deviceType.deviceTypeName',
          'status',
          'supportsKep',
        ],
      }}
    />
  );
}
